<template>
  <div class="kids-carousel-container">
    <Carousel
      ref="carousel"
      v-model="currentSlide"
      :items-to-show="itemsToShow"
      :wrap-around="true"
      snap-align="center"
    >
      <Slide v-for="club in clubs" :key="club.id">
        <div class="kids-card">
          <img :src="club.img" :alt="club.title" class="kids-card-img" />
          <div class="kids-card-content">
            <h3 class="kids-card-title">{{ club.title }}</h3>
            <p class="kids-card-description" v-html="club.description"></p>
          </div>
        </div>
      </Slide>
    </Carousel>

    <div class="pagination">
      <button
        v-for="(_, index) in clubs"
        :key="index"
        @click="goToSlide(index)"
        :class="{ active: currentSlide === index }"
      >
      </button>
    </div>
  </div>
</template>

<script>
import { Carousel, Slide } from 'vue3-carousel'
import 'vue3-carousel/dist/carousel.css'
import { ref, onMounted, onBeforeUnmount } from 'vue'

export default {
  components: { Carousel, Slide },
  setup() {
    const carousel = ref(null)
    const currentSlide = ref(0)
    const itemsToShow = ref(3)
    const clubs = ref([
      { id: 1, img: 'https://monglish.b-cdn.net/kids-landing/clubs/1.png', title: 'Reading Club', description: 'Students are assigned a story and practice reading it aloud, using an attached audio guide to support pronunciation. Once they\'re ready, they participate in comprehension activities and read part of the story to a specialized teacher for evaluation and feedback — helping them grow through targeted improvement tips.' },
      { id: 2, img: 'https://monglish.b-cdn.net/kids-landing/clubs/5.png', title: 'Cinema Club', description: 'In this club, students learn English phrases daily by watching short scenes from films. The focus is on mastering accurate pronunciation. After viewing, students record the phrases, and teachers review and correct them via WhatsApp groups.' },
      { id: 3, img: 'https://monglish.b-cdn.net/kids-landing/clubs/8.png', title: 'Debate Club', description: 'A monthly competition between two classes at the same level. Students debate various topics in English under the supervision of their teachers — promoting critical thinking and spontaneous language use.' },
      { id: 4, img: 'https://monglish.b-cdn.net/kids-landing/clubs/9.png', title: 'Talent Club', description: 'This club enhances children\'s self-confidence by allowing them to express their unique talents in English — such as performance or special skills — using correct language and grammar.' },
      { id: 5, img: 'https://monglish.b-cdn.net/kids-landing/clubs/2.png', title: 'Writing Club', description: 'In this club, students learn academic writing and how to write different text types in English. They are guided to write based on their level and abilities, focusing on foundational writing skills.' },
      { id: 6, img: 'https://monglish.b-cdn.net/kids-landing/clubs/6.png', title: 'Phonetics Club', description: 'This club supports students facing pronunciation difficulties. A specialized teacher trains them on proper articulation and phonetic techniques, using interactive exercises that enhance pronunciation accuracy and fluency.' },
      { id: 7, img: 'https://monglish.b-cdn.net/kids-landing/clubs/3.png', title: 'Life Coach Club', description: 'This club focuses on developing life skills through specialized coaching programs. Trainers help students understand themselves and others, building a strong and balanced personality. Sessions are divided by age group and topic, ensuring age-appropriate guidance.' },
      { id: 8, img: 'https://monglish.b-cdn.net/kids-landing/clubs/2.png', title: 'Drawing Club', description: 'This club provides a free space for children to practice their artistic talents. Children express themselves by creating videos (e.g., on YouTube) about their drawings while describing them in English.' },
      { id: 9, img: 'https://monglish.b-cdn.net/kids-landing/clubs/10.png', title: 'Social Club', description: 'This club helps children understand behavior and how to deal with challenges. Through interactive games and behavioral scenarios, kids learn how to identify emotions and respond with effective strategies for communication.' },
      { id: 10, img: 'https://monglish.b-cdn.net/kids-landing/clubs/11.png', title: 'Singing Club', description: 'An interactive audio space combining music and English learning. The club aims to develop rhythm and listening skills. Students practice pronunciation and build confidence through singing, making learning smoother and more fun.' },
      { id: 11, img: 'https://monglish.b-cdn.net/kids-landing/clubs/4.png', title: 'Conversation Club', description: 'A free space for students to practice what they\'ve learned during lessons. Through daily interactive dialogues, they converse with classmates, guided by teachers. After each session, students receive personalized tasks based on their performance to further improve their conversation skills.' }
    ]);

    let autoplayInterval = null;

    const updateItemsToShow = () => {
      itemsToShow.value = window.innerWidth < 768 ? 1 : window.innerWidth < 1024 ? 2 : 3;
    };

    const goToSlide = (index) => {
      currentSlide.value = index;
    };

   const autoplay = () => {
      currentSlide.value = (currentSlide.value + 1) % clubs.value.length;
    };

    onMounted(() => {
      updateItemsToShow();
      window.addEventListener('resize', updateItemsToShow);
      autoplayInterval = setInterval(autoplay, 3000);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('resize', updateItemsToShow);
      clearInterval(autoplayInterval);
    });

    return {
      carousel,
      currentSlide,
      itemsToShow,
      clubs,
      goToSlide,
    };
  },
};
</script>

<style scoped>
.kids-carousel-container {
  width: 95%;
  margin: 0 auto;
  direction: ltr;
}
.kids-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start; 
  text-align: left;
  padding: 1rem;
  background: #fff;
  border-radius: 1em;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  margin: 30px 10px; 
  font-family: 'DIN Next LT Arabic', sans-serif;
  font-weight: 500;
  width: 100%;
  height: 95%;
}

.kids-card:hover {
  background: linear-gradient(111.84deg, #2c80ac 0%, #165e84 100%);
  color: #ffffff;
}

.kids-card:hover .kids-card-title,
.kids-card:hover .kids-card-description {
  color: #ffffff;
}

.kids-card-img {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 0.5em;
  margin-right: auto;
}

.kids-card-content {
  height: auto;
  padding: 0 1rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.kids-card-title {
  font-size: 1.5rem;
  font-weight: 500;
  line-height: 30px;
  font-family: "DIN Next LT Arabic";
}

.kids-card-description {
  font-size: 1.25em;
  color: #6b6b6b;
}

.pagination {
  display: flex;
  justify-content: center;
  margin: 1rem 0;
}

.pagination button {
  margin: 0 0.5rem;
  padding: 0.5rem 0.5rem;
  border: none;
  background-color: #e0e0e0;
  border-radius: 0.5rem;
  cursor: pointer;
}

.pagination button.active {
  background-color: #165e84;
  padding: 0.5rem 1rem;
}

@media (max-width: 768px) {
  .kids-card {
    width: 100%;
  }

  .pagination button {
    margin: 0 0.25rem;
    padding: 0.4rem 0.4rem;
  }

  .pagination button.active {
    padding: 0.4rem 0.8rem;
  }

  .kids-card-content {
    padding: 0rem;
  }
  .kids-card-title {
    font-size: 1.3rem;
    line-height: 20px;
    margin-bottom: 0;
  }
}
@media (min-width: 768px) {
  .kids-card {
    width: 95%;
  }
}
@media (min-width: 1024px) {
  .kids-card {
    width: 95%; 
  }
}
</style>
